import { useState, useCallback, useRef } from "react";
import { tokenStorage } from "../../../utils/auth/tokenStorage";
import config from "../../../config/environment";
import { trpc } from "../../../lib/api/trpc";

export const useUploadNewVersion = () => {
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const utils = trpc.useContext();
  const progressRef = useRef(setProgress);
  progressRef.current = setProgress;

  const upload = useCallback(
    (documentId: string, file: File): Promise<{ documentId: string }> => {
      setIsUploading(true);
      setProgress(0);
      setError(null);

      return new Promise((resolve, reject) => {
        const token = tokenStorage.getAuthToken();
        if (!token) {
          setIsUploading(false);
          setError("Not authenticated");
          reject(new Error("Not authenticated"));
          return;
        }

        const formData = new FormData();
        formData.append("file", file);

        const xhr = new XMLHttpRequest();
        xhr.open(
          "POST",
          `${config.api.url}/api/documents/${documentId}/versions`,
        );
        xhr.setRequestHeader("Authorization", `Bearer ${token}`);

        xhr.upload.addEventListener("progress", (e) => {
          if (e.lengthComputable) {
            progressRef.current(Math.round((e.loaded / e.total) * 100));
          }
        });

        xhr.addEventListener("load", async () => {
          try {
            const data = JSON.parse(xhr.responseText);
            if (xhr.status >= 200 && xhr.status < 300 && data.document) {
              // Invalidate caches
              try {
                // @ts-ignore
                await utils.documents.list.invalidate();
                // @ts-ignore
                await utils.documents.getQuota.invalidate();
              } catch {
                // non-critical
              }
              setIsUploading(false);
              setProgress(100);
              resolve({ documentId: data.document.id });
            } else {
              const msg = data.error || `Upload failed (${xhr.status})`;
              setError(msg);
              setIsUploading(false);
              reject(new Error(msg));
            }
          } catch {
            setError(`Upload failed (${xhr.status})`);
            setIsUploading(false);
            reject(new Error(`Upload failed (${xhr.status})`));
          }
        });

        xhr.addEventListener("error", () => {
          setError("Network error");
          setIsUploading(false);
          reject(new Error("Network error"));
        });

        xhr.send(formData);
      });
    },
    [utils],
  );

  return { upload, isUploading, progress, error };
};
